import { DefaultChatTransport } from "ai";
import { CORRELATION_ID_HEADER } from "@/lib/constants/api";
import type { AwaitingPlayerSignal } from "@/lib/core/schemas/kernel";
import { ChatSDKError } from "@/lib/errors";

/** An SSE event emitted by the agent service for tool lifecycle. */
export type ToolStatusEvent = {
  type: "tool_start" | "tool_complete";
  tool: string;
  /** LLM-assigned per-invocation ID (ADK ToolContext.functionCallId). Optional:
   *  some lifecycle-info emissions don't have a function call context. Action-
   *  resolving tool_complete events always populate it. */
  toolCallId?: string;
  ok?: boolean;
  errorText?: string;
  /** Game state domains invalidated by this tool call. Present on tool_complete only. */
  invalidates?: readonly string[];
  /**
   * Short, human-readable detail extracted from the tool call's args (e.g.
   * "pack_melee → clearing-wolf-2" for drive_behaviour) — present on both
   * tool_start and tool_complete, and only for tools `computeToolDetail`
   * (services/agent/event-machine/states.ts) explicitly handles. `undefined`
   * for every other tool.
   */
  detail?: string;
  timestamp: number;
};

/**
 * SSE event emitted when the agent terminates a turn because the player
 * owes an interactive input (attack roll, save, damage reveal, etc).
 * Structured mirror of `MachineResult.pauseSignal` plus the originating
 * toolCallId so the frontend can focus the relevant card.
 */
export type AwaitingPlayerEvent = AwaitingPlayerSignal & {
  type: "awaiting-player";
  toolCallId: string;
};

/**
 * Classify a single SSE line for the custom-frame filter.
 *
 * Pure function — no side effects. Exported so it can be unit-tested
 * independently of the transport's fetch machinery.
 *
 * Only the agent's CUSTOM frames are intercepted/stripped; everything the
 * Vercel AI SDK understands natively (text deltas, `[DONE]`, and the SDK's own
 * `{ type: "error", errorText }` error part) is passed through so the SDK can
 * handle it. In particular an error frame is the SDK's native error part —
 * passing it through makes useChat surface a real error state (its `onError` +
 * error banner); stripping it would silently truncate the response instead.
 *
 * Returns:
 * - `{ kind: "tool"; data: unknown }` — a `tool_start` or `tool_complete`
 *   frame; caller forwards to `onToolEvent` then strips from SDK stream.
 * - `{ kind: "awaiting-player"; data: unknown }` — a pause-signal frame;
 *   caller forwards to `onAwaitingPlayer` then strips.
 * - `{ kind: "passthrough" }` — everything else (incl. the SDK-native error
 *   part); caller enqueues as-is.
 */
export function classifySseLine(
  line: string
):
  | { kind: "tool"; data: unknown }
  | { kind: "awaiting-player"; data: unknown }
  | { kind: "passthrough" } {
  if (!line.startsWith("data: ")) {
    return { kind: "passthrough" };
  }
  const dataStr = line.slice(6);
  if (dataStr === "[DONE]") {
    return { kind: "passthrough" };
  }
  try {
    const data = JSON.parse(dataStr);
    if (
      data &&
      typeof data === "object" &&
      (data.type === "tool_start" || data.type === "tool_complete")
    ) {
      return { kind: "tool", data };
    }
    if (data && typeof data === "object" && data.type === "awaiting-player") {
      return { kind: "awaiting-player", data };
    }
  } catch {
    // Not JSON or partial JSON — pass through
  }
  return { kind: "passthrough" };
}

/**
 * True when the agent's /chat response is a 429 rate-limit denial (spec
 * §11.2). Both the budget-check 429 and the pre-existing concurrency-gate
 * 429 map to the same "rate_limit:chat" copy — that conflation is accepted
 * (both mean "wait and retry").
 */
export function isRateLimitResponse(response: Response): boolean {
  return response.status === 429;
}

/**
 * Creates a chat transport that intercepts legacy SSE tool events
 * (`tool_start` / `tool_complete`), forwards them to `onToolEvent` for
 * real-time UI updates, and strips them from the stream so they don't
 * confuse the Vercel AI SDK.
 *
 * The native UIMessage protocol events (tool-input-available /
 * tool-output-available) are passed through unchanged for the SDK to
 * populate message `parts` on completed messages.
 *
 * When `options.getStreamingUrl` returns a URL, the request is sent directly
 * to the Cloud Run service (bypassing Firebase Hosting CDN buffering). In
 * that case `options.getIdToken` must return a Firebase ID token for auth.
 * Both callbacks are invoked fresh on every request so stale values are never
 * used.
 */
export function createToolAwareTransport(
  onToolEvent?: (event: ToolStatusEvent) => void,
  options?: {
    /**
     * Returns the Cloud Run URL to call directly, bypassing Firebase Hosting.
     * Called on every request so the URL can be loaded asynchronously after
     * transport creation without recreating the transport object.
     */
    getStreamingUrl?: () => string | undefined;
    /** Returns a fresh Firebase ID token for Bearer auth on direct requests. */
    getIdToken?: () => Promise<string | undefined>;
    /**
     * Callback fired when the agent terminates a turn because the player
     * owes an interactive input. The frontend can use this to focus the
     * relevant action card, play a sound, etc. Optional — the existing
     * Fetch + Invalidate cycle on `tool_complete` already re-hydrates
     * `gameState.pendingActions`, so the card renders without any
     * explicit handling here.
     */
    onAwaitingPlayer?: (event: AwaitingPlayerEvent) => void;
  }
) {
  const customFetch: typeof fetch = async (_input, init) => {
    // Resolve the streaming URL fresh on each request. Falls back to the
    // same-origin relative path (via Firebase Hosting) when not yet available.
    const streamingUrl = options?.getStreamingUrl?.();
    const url = streamingUrl
      ? `${streamingUrl}/api/agent/chat`
      : "/api/agent/chat";

    let fetchInit = init;
    if (streamingUrl && options?.getIdToken) {
      const idToken = await options.getIdToken();
      if (idToken) {
        const headers = new Headers(init?.headers);
        headers.set("Authorization", `Bearer ${idToken}`);
        fetchInit = { ...init, headers };
      }
    }

    const response = await fetch(url, fetchInit);

    if (isRateLimitResponse(response)) {
      throw new ChatSDKError("rate_limit:chat");
    }

    if (!response.ok || !response.body) {
      return response;
    }

    // ── SSE Trace: browser-side chunk timing ────────────────────────
    // Enabled automatically when the server sets the correlation ID header
    // (only when SSE_TRACE=1 on the server side). No browser env var needed.
    const traceId = response.headers.get(CORRELATION_ID_HEADER);
    let browserSeq = 0;
    const streamStartMs = traceId ? performance.now() : 0;

    const transformStream = new TransformStream({
      start(_controller) {
        this.buffer = "";
      },
      transform(chunk, controller) {
        if (traceId) {
          // biome-ignore lint/suspicious/noConsole: SSE diagnostic tracing
          console.debug("[SSE-Trace] browser", {
            correlationId: traceId,
            layer: "browser",
            seq: browserSeq++,
            elapsedMs: Math.round(performance.now() - streamStartMs),
            bytes: chunk.byteLength,
            preview: new TextDecoder().decode(chunk.slice(0, 120)),
          });
        }

        const text = new TextDecoder().decode(chunk);
        this.buffer += text;

        const lines = this.buffer.split("\n");
        this.buffer = lines.pop() || "";

        for (const line of lines) {
          const classified = classifySseLine(line);
          if (classified.kind === "tool") {
            // Forward to callback for real-time UI, then strip from SDK stream
            onToolEvent?.(classified.data as ToolStatusEvent);
            continue;
          }
          if (classified.kind === "awaiting-player") {
            // Custom pause signal — forward for UI affordance then strip.
            // The AI SDK's UIMessage union doesn't know this type; leaving
            // it in the stream makes useChat throw a Zod validation error.
            options?.onAwaitingPlayer?.(classified.data as AwaitingPlayerEvent);
            continue;
          }
          // Everything else (text deltas, [DONE], and the SDK's native
          // `{ type: "error", errorText }` part) passes through untouched so
          // the AI SDK handles it — an error part surfaces useChat's error
          // state rather than being silently dropped.
          controller.enqueue(new TextEncoder().encode(`${line}\n`));
        }
      },
      flush(controller) {
        if (this.buffer) {
          controller.enqueue(new TextEncoder().encode(`${this.buffer}\n`));
        }
      },
    } as Transformer<Uint8Array, Uint8Array> & { buffer: string });

    return new Response(response.body.pipeThrough(transformStream), {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    });
  };

  return new DefaultChatTransport({
    api: "/api/agent/chat",
    fetch: customFetch,
  });
}
